import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Heart, ArrowLeft, Briefcase } from 'lucide-react';

export default function CareersPage() {
  const navigate = useNavigate();

  const positions = [
    {
      title: 'Home Companion',
      type: 'Part-time / Full-time',
      description: 'Offer meaningful companionship and support to individuals in their homes.',
      requirements: ['Compassionate heart', 'Reliable transportation', 'Background check required'],
      applicationLink: '/careers/dsp'
    },
    {
      title: 'CNA (Certified Nursing Assistant)',
      type: 'Part-time / Full-time',
      description: 'Assist with daily living activities and personal care with dignity and respect.',
      requirements: ['CNA certification required', 'Experience in caregiving', 'Valid driver\'s license'],
      applicationLink: '/careers/cna'
    },
    {
      title: 'Registered Nurse (RN)',
      type: 'Full-time',
      description: 'Provide compassionate nursing care in home settings with clinical excellence.',
      requirements: ['Current NC RN License', 'BSN preferred', 'Home health experience a plus'],
      applicationLink: null
    },
    {
      title: 'Administrative Support',
      type: 'Full-time',
      description: 'Support our mission through office operations and client coordination.',
      requirements: ['Office experience', 'Strong communication skills', 'Detail-oriented'],
      applicationLink: null
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-purple-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_caregiver-command/artifacts/prj1fr46_Integrity%20Home%20Care%20Logo%20Webpage.png"
                alt="Integrity Home Care LLC"
                className="h-16 w-auto"
              />
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-white to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-purple-100 rounded-full">
              <Briefcase className="w-12 h-12 text-purple-600" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-purple-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Join Our Team
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
            Be part of a mission-driven team that ministers to the community with a servant's heart. 
            At Integrity Home Care LLC, we honor the dignity of every individual while providing compassionate care.
          </p>
          <div className="bg-purple-100 border-l-4 border-purple-600 p-6 rounded-r-lg max-w-3xl mx-auto">
            <p className="text-purple-900 italic text-lg">
              "Even as the Son of man came not to be ministered unto, but to minister, and to give his life a ransom for many."
            </p>
            <p className="text-purple-700 text-sm mt-2 font-semibold">— Matthew 20:28</p>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-purple-900 mb-12 text-center" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Why Work With Us?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6 border-purple-100 text-center">
              <div className="flex justify-center mb-4">
                <Heart className="w-12 h-12 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-purple-900 mb-3">Faith-Based Mission</h3>
              <p className="text-gray-700">
                Work in an environment where faith, compassion, and professional excellence come together.
              </p>
            </Card>
            <Card className="p-6 border-purple-100 text-center">
              <div className="flex justify-center mb-4">
                <Heart className="w-12 h-12 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-purple-900 mb-3">Meaningful Impact</h3>
              <p className="text-gray-700">
                Make a real difference in the lives of individuals and families in your community.
              </p>
            </Card>
            <Card className="p-6 border-purple-100 text-center">
              <div className="flex justify-center mb-4">
                <Heart className="w-12 h-12 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-purple-900 mb-3">Supportive Team</h3>
              <p className="text-gray-700">
                Join a team that values you and supports your professional growth and development.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Available Positions */}
      <section className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-purple-900 mb-12 text-center" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Current Openings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {positions.map((position, index) => (
              <Card key={index} className="p-8 border-purple-100 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-purple-900 mb-2">{position.title}</h3>
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                      {position.type}
                    </span>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">{position.description}</p>
                <div className="mb-6">
                  <h4 className="font-semibold text-purple-900 mb-2">Requirements:</h4>
                  <ul className="space-y-1">
                    {position.requirements.map((req, idx) => (
                      <li key={idx} className="text-gray-700 text-sm flex items-start">
                        <span className="text-purple-600 mr-2">•</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button
                  onClick={() => position.applicationLink ? navigate(position.applicationLink) : null}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={!position.applicationLink}
                >
                  {position.applicationLink ? 'Apply Now' : 'Application Coming Soon'}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-purple-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Questions About Employment?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            We'd love to hear from you! Contact us to learn more about career opportunities.
          </p>
          <div className="space-y-4">
            <p className="text-lg">
              <span className="font-semibold text-purple-900">Phone:</span>{' '}
              <a href="tel:+15716062347" className="text-purple-600 hover:text-purple-700">(571) 606-2347</a>
            </p>
            <p className="text-lg">
              <span className="font-semibold text-purple-900">Email:</span>{' '}
              <a href="mailto:info@integrityhomecarenc.com" className="text-purple-600 hover:text-purple-700">
                info@integrityhomecarenc.com
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-purple-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-purple-200">© 2024 Integrity Home Care LLC. All rights reserved.</p>
          <p className="mt-2 italic text-sm text-purple-300">
            "Even as the Son of man came not to be ministered unto, but to minister." - Matthew 20:28
          </p>
        </div>
      </footer>
    </div>
  );
}
